import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Bell, CheckCheck, ChevronDown, Clock3, Loader2, Settings2 } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { Button } from './ui/button'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { ScrollArea } from './ui/scroll-area'
import { cn } from '../lib/utils'
import { formatModelLabel } from '../lib/modelLabel'

type NotificationRow = {
  id: string
  team_id: string
  source_type: string
  payload: Record<string, unknown>
  read_at: string | null
  created_at: string
  team?: { name: string } | null
}

type NotificationFilter = 'all' | 'unread'
type NotificationGroup = {
  id: string
  notification: NotificationRow
  notifications: NotificationRow[]
  memberKeys: Set<string>
  memberNames: Set<string>
}

const sourceMeta: Record<string, { label: string; tone: string }> = {
  budget_threshold: { label: 'Billing', tone: 'bg-red-500' },
  standup_escalation: { label: 'Standups', tone: 'bg-red-500' },
  anomaly: { label: 'Usage', tone: 'bg-amber-500' },
  manager_digest: { label: 'Digest', tone: 'bg-cyan-500' },
  model_usage: { label: 'Model usage', tone: 'bg-violet-500' },
}

function notificationCopy(notification: NotificationRow) {
  const payload = notification.payload ?? {}
  const teamName = String(payload.team_name ?? payload.pod_name ?? notification.team?.name ?? 'your team')
  const spend = String(payload.spend_usd_estimated ?? '0')
  const limit = String(payload.monthly_limit_usd ?? '0')
  const percent = Number(payload.budget_percent ?? (Number(limit) ? Number(spend) / Number(limit) * 100 : 0))

  switch (notification.source_type) {
    case 'budget_threshold':
      const remaining = Number(limit) - Number(spend)
      return {
        category: 'Billing',
        headline: `${teamName} budget at ${Math.round(percent)}% - $${spend} of $${limit} spent`,
        description: `${teamName} has ${remaining >= 0 ? `$${remaining.toFixed(2)} remaining` : `$${Math.abs(remaining).toFixed(2)} over budget`}. ${String(payload.top_spender_name ?? payload.user_name ?? 'The team')} is today's top spender with $${String(payload.top_spender_cost_usd ?? spend)} across ${String(payload.top_spender_tokens ?? 'recent')} tokens.`,
      }
    case 'standup_escalation':
      if (payload.notification_kind === 'reminder') {
        return {
          category: 'Standups',
          headline: 'Standup check-in reminder',
          description: `Your check-in deadline is coming up in about ${String(payload.reminder_lead_minutes ?? 30)} minutes.`,
        }
      }
      return {
        category: 'Standups',
        headline: payload.missed_count && Number(payload.missed_count) > 1
          ? `${String(payload.missed_count)} team members missed today's check-in window`
          : `${String(payload.missed_names ?? payload.user_name ?? 'A team member')} missed today's check-in window`,
        description: `${teamName}: ${String(payload.missed_names ?? payload.user_name ?? 'A team member')} did not submit a standup before the check-in deadline${payload.missed_count && Number(payload.missed_count) > 1 ? '. Review the missed check-ins together.' : payload.consecutive_misses ? ` and has missed ${String(payload.consecutive_misses)} consecutive check-ins.` : '.'}`,
      }
    case 'anomaly':
      return {
        category: 'Usage',
        headline: `${String(payload.user_name ?? 'A team member')}'s usage is running ${String(payload.percent_above_usual ?? 'above usual')}% above usual`,
        description: `${teamName} · ${String(payload.activity ?? 'Review recent usage activity and cost drivers.')}`,
      }
    case 'manager_digest':
      return {
        category: 'Digest',
        headline: `${String(payload.report_name ?? 'Team')} activity digest is ready`,
        description: String(payload.needs_review ?? 'Review the latest team activity and connector usage.'),
      }
    case 'model_usage': {
      const model = formatModelLabel(typeof payload.model_id === 'string' ? payload.model_id : null)
      const userName = String(payload.user_name ?? 'A team member')
      const eventCount = Number(payload.event_count ?? 1)
      const cost = payload.cost_usd == null ? null : Number(payload.cost_usd)
      if (payload.notification_kind === 'daily_summary') {
        return {
          category: 'Model usage',
          headline: `${model} daily usage summary`,
          description: `${eventCount} use${eventCount === 1 ? '' : 's'} across ${String(payload.person_count ?? 'the team')} ${String(payload.person_count === 1 ? 'person' : 'people')}${cost != null && Number.isFinite(cost) ? ` · $${cost.toFixed(2)} estimated cost` : ''}.`,
        }
      }
      return {
        category: 'Model usage',
        headline: `${userName} used ${model}`,
        description: `${teamName}${cost != null && Number.isFinite(cost) ? ` · $${cost.toFixed(2)} estimated cost` : ''}. Additional usage today will be aggregated.`,
      }
    }
    default:
      return {
        category: sourceMeta[notification.source_type]?.label ?? 'Notifications',
        headline: 'New SyncSimple notification',
        description: 'A new alert is ready to review.',
      }
  }
}

function reviewPath(notification: NotificationRow) {
  const payload = notification.payload ?? {}
  const userId = typeof payload.user_id === 'string' ? payload.user_id : null
  const reportUserId = typeof payload.report_user_id === 'string' ? payload.report_user_id : null
  const date = typeof payload.standup_date === 'string'
    ? payload.standup_date
    : typeof payload.alert_date === 'string'
      ? payload.alert_date
      : null

  switch (notification.source_type) {
    case 'standup_escalation':
      if (payload.notification_kind === 'reminder' && userId) {
        return `/dashboard/team/${notification.team_id}/standups/${userId}${date ? `?date=${encodeURIComponent(date)}` : ''}`
      }
      return `/dashboard/team/${notification.team_id}/standups/check-ins${date ? `?date=${encodeURIComponent(date)}&filter=missed` : '?filter=missed'}`
    case 'manager_digest':
      return reportUserId
        ? `/dashboard/team/${notification.team_id}/standups/${reportUserId}${date ? `?date=${encodeURIComponent(date)}` : ''}`
        : `/dashboard/team/${notification.team_id}/standups/summary`
    case 'budget_threshold':
      return `/dashboard/team/${notification.team_id}/alerts?section=budgets`
    case 'anomaly':
      return userId
        ? `/dashboard/team/${notification.team_id}/connectors?user=${encodeURIComponent(userId)}${date ? `&date=${encodeURIComponent(date)}` : ''}`
        : `/dashboard/team/${notification.team_id}/connectors`
    case 'model_usage':
      return `/dashboard/team/${notification.team_id}/alerts?section=model-usage`
    default:
      return `/dashboard/team/${notification.team_id}`
  }
}

interface NotificationBellProps {
  teamId?: string
}

export function NotificationBell({ teamId: _teamId }: NotificationBellProps) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState<NotificationRow[]>([])
  const [ignoredIds, setIgnoredIds] = useState<Set<string>>(new Set())
  const [filter, setFilter] = useState<NotificationFilter>('all')
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [open, setOpen] = useState(false)
  const notificationCountRef = useRef(0)

  const loadNotifications = useCallback(async (append = false) => {
    if (!user) {
      setNotifications([])
      notificationCountRef.current = 0
      setHasMore(false)
      return
    }

    if (append) setLoadingMore(true)
    else setLoading(true)
    try {
      const from = append ? notificationCountRef.current : 0
      const to = from + 29
      const { data, error } = await (supabase as any)
        .from('notifications')
        .select('id, team_id, source_type, payload, read_at, created_at, team:teams(name)')
        .eq('recipient_user_id', user.id)
        .order('created_at', { ascending: false })
        .range(from, to)

      if (error) throw error
      const next = (data ?? []) as NotificationRow[]
      notificationCountRef.current = append ? from + next.length : next.length
      setNotifications((current) => append ? [...current, ...next] : next)
      setHasMore(next.length === 30)
    } catch (error) {
      console.error('Failed to load notifications:', error)
    } finally {
      if (append) setLoadingMore(false)
      else setLoading(false)
    }
  }, [user])

  useEffect(() => { void loadNotifications() }, [loadNotifications])

  useEffect(() => {
    const interval = window.setInterval(() => void loadNotifications(), 60_000)
    return () => window.clearInterval(interval)
  }, [loadNotifications])

  const unreadCount = useMemo(() => notifications.filter((notification) => !notification.read_at).length, [notifications])
  const visibleNotifications = useMemo(() => notifications.filter((notification) => {
    if (ignoredIds.has(notification.id)) return false
    return filter === 'all' || !notification.read_at
  }), [filter, ignoredIds, notifications])

  const notificationGroups = useMemo<NotificationGroup[]>(() => {
    const groups: NotificationGroup[] = []
    const escalationGroups = new Map<string, NotificationGroup>()
    for (const notification of visibleNotifications) {
      const payload = notification.payload ?? {}
      const isEscalation = notification.source_type === 'standup_escalation' && payload.notification_kind !== 'reminder'
      const date = typeof payload.standup_date === 'string' ? payload.standup_date : notification.created_at.slice(0, 10)
      if (!isEscalation) {
        groups.push({ id: notification.id, notification, notifications: [notification], memberKeys: new Set(), memberNames: new Set() })
        continue
      }
      const key = `${notification.team_id}:${date}`
      const existing = escalationGroups.get(key)
      if (existing) {
        existing.notifications.push(notification)
        const memberKey = typeof payload.user_id === 'string' ? payload.user_id : String(payload.user_name ?? notification.id)
        const userName = typeof payload.user_name === 'string' ? payload.user_name : 'A team member'
        existing.memberKeys.add(memberKey)
        existing.memberNames.add(userName)
        const existingPayload = existing.notification.payload ?? {}
        existing.notification = {
          ...existing.notification,
          read_at: existing.notifications.some((item) => !item.read_at) ? null : existing.notification.read_at,
          payload: { ...existingPayload, missed_count: existing.memberKeys.size, missed_names: Array.from(existing.memberNames).join(', ') },
        }
      } else {
        const memberKey = typeof payload.user_id === 'string' ? payload.user_id : String(payload.user_name ?? notification.id)
        const memberName = typeof payload.user_name === 'string' ? payload.user_name : 'A team member'
        const group: NotificationGroup = {
          id: `standup:${key}`,
          notification: { ...notification, payload: { ...payload, missed_count: 1, missed_names: memberName } },
          notifications: [notification],
          memberKeys: new Set([memberKey]),
          memberNames: new Set([memberName]),
        }
        escalationGroups.set(key, group)
        groups.push(group)
      }
    }
    return groups
  }, [visibleNotifications])

  const updateReadState = async (ids: string[], readAt: string | null) => {
    if (!user || ids.length === 0) return
    const { error } = await (supabase as any)
      .from('notifications')
      .update({ read_at: readAt })
      .in('id', ids)
      .eq('recipient_user_id', user.id)
    if (error) throw error
  }

  const markAsRead = async (notification: NotificationRow): Promise<boolean> => {
    if (notification.read_at) return true
    const readAt = new Date().toISOString()
    setNotifications((current) => current.map((item) => item.id === notification.id ? { ...item, read_at: readAt } : item))
    try {
      await updateReadState([notification.id], readAt)
      return true
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
      setNotifications((current) => current.map((item) => item.id === notification.id ? { ...item, read_at: null } : item))
      return false
    }
  }

  const markAllAsRead = async () => {
    const unread = notifications.filter((notification) => !notification.read_at)
    if (!unread.length) return
    const readAt = new Date().toISOString()
    setNotifications((current) => current.map((item) => ({ ...item, read_at: item.read_at ?? readAt })))
    try {
      await updateReadState(unread.map((notification) => notification.id), readAt)
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error)
      await loadNotifications()
    }
  }

  const ignore = async (group: NotificationGroup) => {
    const read = await Promise.all(group.notifications.map((notification) => markAsRead(notification)))
    if (read.every(Boolean)) {
      setIgnoredIds((current) => new Set([...current, ...group.notifications.map((notification) => notification.id)]))
    }
  }

  const review = async (group: NotificationGroup) => {
    await Promise.all(group.notifications.map((notification) => markAsRead(notification)))
    setOpen(false)
    navigate(reviewPath(group.notification))
  }

  return (
    <Popover open={open} onOpenChange={(nextOpen) => { setOpen(nextOpen); if (nextOpen) void loadNotifications() }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={unreadCount ? `${unreadCount} unread notifications` : 'Notifications'} className="relative size-10 rounded-full">
          <Bell className="h-[19px] w-[19px] text-muted-foreground" />
          {unreadCount > 0 && <span className="absolute right-1 top-1 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-4 text-destructive-foreground">{unreadCount > 9 ? '9+' : unreadCount}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-[min(420px,calc(100vw-1rem))] overflow-hidden border-border bg-background p-0 shadow-lg">
        <div className="flex items-center justify-between border-b px-4 py-3.5">
          <h2 className="text-xl font-semibold tracking-[-0.03em] text-foreground">Notifications</h2>
          <Button variant="ghost" size="icon" disabled aria-label="Notification settings coming soon" title="Notification settings coming soon" className="size-8 text-muted-foreground"><Settings2 className="h-4 w-4" /></Button>
        </div>
        <div className="flex items-center gap-3 border-b px-4 py-2.5">
          <button type="button" onClick={() => setFilter('all')} className={cn('rounded-full px-2.5 py-1 text-xs transition-colors', filter === 'all' ? 'border border-cyan-400 bg-cyan-50 text-cyan-700' : 'text-muted-foreground hover:text-foreground')}>All</button>
          <button type="button" onClick={() => setFilter('unread')} className={cn('text-xs transition-colors', filter === 'unread' ? 'font-semibold text-cyan-700' : 'text-muted-foreground hover:text-foreground')}>Unread ({unreadCount})</button>
          <button type="button" onClick={() => void markAllAsRead()} disabled={!unreadCount} className="ml-auto text-[11px] font-semibold text-primary transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40">Mark as read</button>
        </div>
        <ScrollArea className="h-[min(480px,58vh)]">
          {loading && notificationGroups.length === 0 ? (
            <div className="flex items-center justify-center gap-2 px-6 py-14 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading notifications…</div>
          ) : notificationGroups.length === 0 ? (
            <div className="px-6 py-14 text-center text-sm text-muted-foreground">{filter === 'unread' ? 'You’re all caught up.' : 'No notifications yet.'}</div>
          ) : (
            <div className="px-4">
              {notificationGroups.map((group) => {
                const notification = group.notification
                const copy = notificationCopy(notification)
                const meta = sourceMeta[notification.source_type]
                return (
                  <article key={notification.id} className={cn('border-b py-4 last:border-b-0', !notification.read_at && 'bg-cyan-50/[0.18]')}>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground"><span className={cn('h-2 w-2 shrink-0 rounded-full', notification.read_at ? 'bg-muted' : meta?.tone ?? 'bg-cyan-500')} /><span>{copy.category} · {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}</span></div>
                    <h3 className={cn('mt-2 text-sm font-semibold leading-snug tracking-[-0.01em]', notification.read_at ? 'text-muted-foreground' : 'text-foreground')}>{copy.headline}</h3>
                    <p className="mt-1.5 max-w-[390px] text-xs leading-[1.45] text-muted-foreground">{copy.description}</p>
                    <div className="mt-2.5 flex items-center gap-2">
                      <Button size="sm" onClick={() => void review(group)} className="h-7 rounded-md bg-cyan-500 px-3 text-xs text-slate-950 hover:bg-cyan-400">Review</Button>
                      <Button size="sm" variant="outline" onClick={() => void ignore(group)} className="h-7 rounded-md px-3 text-xs text-muted-foreground">Ignore</Button>
                      {!notification.read_at && <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-muted-foreground"><Clock3 className="h-3 w-3" /> New</span>}
                      {notification.read_at && <CheckCheck className="ml-auto h-3 w-3 text-muted-foreground" aria-label="Read" />}
                    </div>
                  </article>
                )
              })}
            </div>
          )}
        </ScrollArea>
        {hasMore && notificationGroups.length > 0 && (
          <div className="border-t px-4 py-2.5 text-center">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void loadNotifications(true)}
              disabled={loadingMore}
              className="h-8 text-xs text-muted-foreground hover:text-foreground"
            >
              {loadingMore ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ChevronDown className="mr-1.5 h-3.5 w-3.5" />}
              {loadingMore ? 'Loading older notifications…' : 'Load older notifications'}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
